import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
    X, ShieldCheck, KeyRound, CheckCircle2, 
    AlertCircle, Lock, Eye, EyeOff, Key 
} from 'lucide-react';
import { getAuth, updatePassword } from 'firebase/auth';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useUserData } from './UserDataContext';
import { AuthService } from '../services/api';
import { verifyPIN } from '../services/recoveryService';
import { toast } from 'sonner';
import { Dialog, DialogPortal, DialogOverlay } from './ui/dialog';
import { MODAL, MODAL_ACCENT, MODAL_SPINNER, modalPinBox, modalPrimaryButton } from './ui/unifiedModalStyle';

interface ChangePasswordModalProps {
    onClose: () => void;
}

type VerificationMode = 'password' | 'pin';

/** Local alias so the call sites below stay readable. */
const ACCENT = MODAL_ACCENT;

// ── 4-digit PIN input component (shared logic from SecuritySetupModal) ──────
function PinInput({
    value,
    onChange,
    disabled = false,
}: {
    value: string;
    onChange: (val: string) => void;
    disabled?: boolean;
}) {
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace' && !value[index] && index > 0) {
            const next = value.slice(0, index - 1) + value.slice(index);
            onChange(next);
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handleChange = (index: number, char: string) => {
        if (!/^\d$/.test(char)) return;
        const arr = value.split('').concat(Array(4).fill('')).slice(0, 4);
        arr[index] = char;
        const newVal = arr.join('').replace(/\s/g, '').slice(0, 4);
        onChange(newVal);
        if (index < 3) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const digits = (value + '    ').split('').slice(0, 4);

    // Kept in step with SecuritySetupModal's boxes deliberately: this is the same
    // control in a different modal, and the two had already drifted on size and
    // had no focused state at all.
    const [focused, setFocused] = useState<number | null>(null);

    return (
        <div style={{ display: 'flex', gap: 14, justifyContent: 'center', paddingTop: 4, paddingBottom: 4 }}>
            {digits.map((digit, i) => {
                const filled = !!digit.trim();
                const isFocused = focused === i;
                return (
                    <input
                        key={i}
                        ref={(el) => { inputRefs.current[i] = el; }}
                        type="password"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit.trim()}
                        disabled={disabled}
                        onChange={(e) => handleChange(i, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(i, e)}
                        onFocus={(e) => { setFocused(i); e.target.select(); }}
                        onBlur={() => setFocused(f => (f === i ? null : f))}
                        className="text-center outline-none"
                        style={modalPinBox(filled, isFocused)}
                    />
                );
            })}
        </div>
    );
}

export function ChangePasswordModal({ onClose }: ChangePasswordModalProps) {
    const { userProfile } = useUserData();
    const [mode, setMode] = useState<VerificationMode>('password');
    const [oldPassword, setOldPassword] = useState('');
    const [pin, setPin] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPasswords, setShowPasswords] = useState(false);
    
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [success, setSuccess] = useState(false);

    const handleUpdate = async () => {
        setErrorMsg('');
        
        // Basic validation
        if (mode === 'password' && !oldPassword) {
            setErrorMsg('Please enter your current password.');
            return;
        }
        if (mode === 'pin' && pin.length !== 4) {
            setErrorMsg('Please enter your 4-digit PIN.');
            return;
        }
        if (!newPassword || newPassword.length < 6) {
            setErrorMsg('New password must be at least 6 characters.');
            return;
        }
        if (newPassword !== confirmPassword) {
            setErrorMsg('New passwords do not match.');
            return;
        }

        setLoading(true);
        try {
            const auth = getAuth();
            if (!auth.currentUser) throw new Error("No authenticated user.");

            // 1. Verify identity
            if (mode === 'password') {
                await AuthService.reauthenticate(oldPassword);
            } else {
                const isValidPin = await verifyPIN(userProfile?.id || '', pin);
                if (!isValidPin) {
                    throw new Error("Incorrect PIN. Please try again.");
                }
            }

            // 2. Update password
            await updatePassword(auth.currentUser, newPassword);
            
            setSuccess(true);
            toast.success("Password updated successfully!");
            setTimeout(onClose, 2000);
        } catch (err: any) {
            console.error("Password update error:", err);
            setErrorMsg(err?.message || "Failed to update password. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
            <DialogPortal>
                <div className="fixed inset-0 flex items-center justify-center p-4 overflow-hidden" style={{ zIndex: 99999 }}>
                    <DialogOverlay 
                        className="fixed inset-0 z-0" 
                        style={{ backdropFilter: 'blur(14px)', backgroundColor: 'rgba(0,0,0,0.7)' }} 
                    />
                    
                    <motion.div
                        initial={{ opacity: 0, scale: 0.92, y: 24 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9, y: 20 }}
                        transition={{ type: 'spring', stiffness: 290, damping: 26 }}
                        className="relative z-10 w-full rounded-3xl overflow-hidden shadow-2xl"
                        style={{
                            background: 'linear-gradient(160deg, rgba(13,13,20,0.98) 0%, rgba(8,8,12,0.99) 100%)',
                            border: '1px solid rgba(255,255,255,0.07)',
                            boxShadow: '0 40px 80px rgba(0,0,0,0.65), inset 0 1px 0 rgba(255,255,255,0.04)',
                            // Shell sizing, dvh and scroll behaviour: unifiedModalStyle.
                            ...MODAL.shell,
                        }}
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between sticky top-0 z-10"
                            style={MODAL.header}
                        >
                            <div className="flex items-center gap-4">
                                <KeyRound className="w-5 h-5 text-[#4478ea]" />
                                <h2 className="text-white font-bold text-lg tracking-tight">Change Password</h2>
                            </div>
                            <button
                                onClick={onClose}
                                className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-zinc-400 hover:text-white transition-all"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        <div
                            className="custom-scrollbar"
                            style={MODAL.content}
                        >
                            <AnimatePresence mode="wait">
                                {success ? (
                                    <motion.div
                                        key="success"
                                        initial={{ opacity: 0, scale: 0.8 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        className="flex flex-col items-center text-center py-10"
                                    >
                                        <div className="w-20 h-20 rounded-full bg-green-500/10 flex items-center justify-center mb-5">
                                            <CheckCircle2 className="w-10 h-10 text-green-500" />
                                        </div>
                                        <h3 className="text-white font-bold text-xl mb-2">Password Updated!</h3>
                                        <p className="text-zinc-400 text-sm">Your account is now more secure. Closing...</p>
                                    </motion.div>
                                ) : (
                                    <motion.div
                                        key="form"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        className="space-y-6 pt-2"
                                    >
                                        {/* Verification Section */}
                                        <div className="space-y-4">
                                            <div className="flex items-center justify-between">
                                                <p style={MODAL.label}>
                                                    Verify your identity
                                                </p>
                                                <button 
                                                    onClick={() => {
                                                        setMode(mode === 'password' ? 'pin' : 'password');
                                                        setErrorMsg('');
                                                    }}
                                                    className="text-[#4478ea] text-xs font-medium hover:underline"
                                                >
                                                    {mode === 'password' ? 'Use PIN instead' : 'Use Password instead'}
                                                </button>
                                            </div>

                                            {mode === 'password' ? (
                                                <div className="relative">
                                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                                                    <Input
                                                        type={showPasswords ? 'text' : 'password'}
                                                        placeholder="Current Password"
                                                        value={oldPassword}
                                                        onChange={(e) => setOldPassword(e.target.value)}
                                                        className="pl-10 h-12 rounded-xl bg-white/5 border-white/10 text-white focus:border-blue-500/50"
                                                    />
                                                </div>
                                            ) : (
                                                <div className="space-y-2">
                                                    <p className="text-zinc-500 text-[11px] text-center italic">
                                                        Forgot your password? Enter your 4-digit recovery PIN.
                                                    </p>
                                                    <PinInput value={pin} onChange={setPin} />
                                                </div>
                                            )}
                                        </div>

                                        <div className="h-px bg-white/5 w-full" />

                                        {/* New Password Section */}
                                        <div className="space-y-4">
                                            <p style={MODAL.label}>
                                                New Password
                                            </p>
                                            <div className="space-y-3">
                                                <div className="relative">
                                                    <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                                                    <Input
                                                        type={showPasswords ? 'text' : 'password'}
                                                        placeholder="New Password (min. 6 chars)"
                                                        value={newPassword}
                                                        onChange={(e) => setNewPassword(e.target.value)}
                                                        className="pl-10 h-12 rounded-xl bg-white/5 border-white/10 text-white focus:border-blue-500/50"
                                                    />
                                                </div>
                                                <div className="relative">
                                                    <ShieldCheck className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                                                    <Input
                                                        type={showPasswords ? 'text' : 'password'}
                                                        placeholder="Confirm New Password"
                                                        value={confirmPassword}
                                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                                        className="pl-10 h-12 rounded-xl bg-white/5 border-white/10 text-white focus:border-blue-500/50"
                                                    />
                                                </div>
                                            </div>

                                            <button
                                                type="button"
                                                onClick={() => setShowPasswords(!showPasswords)}
                                                className="flex items-center gap-2 text-zinc-500 hover:text-zinc-300 transition-colors text-xs ml-1"
                                            >
                                                {showPasswords ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                                                {showPasswords ? 'Hide characters' : 'Show characters'}
                                            </button>
                                        </div>

                                        {errorMsg && (
                                            <motion.div
                                                initial={{ opacity: 0, y: -10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 flex items-start gap-3"
                                            >
                                                <AlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                                                <p className="text-red-500 text-xs leading-relaxed">{errorMsg}</p>
                                            </motion.div>
                                        )}

                                        <button
                                            type="button"
                                            onClick={handleUpdate}
                                            disabled={loading}
                                            style={{ ...modalPrimaryButton(!loading), marginTop: 20 }}
                                        >
                                            {loading ? (
                                                <span
                                                    className="animate-spin"
                                                    style={MODAL_SPINNER}
                                                />
                                            ) : (
                                                'Update Password'
                                            )}
                                        </button>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </motion.div>
                </div>
            </DialogPortal>
        </Dialog>
    );
}
